import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { companyInfo, products } from '../mock';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToProduct = (productSlug) => {
    navigate(`/products/${productSlug}`);
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white pt-16 pb-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Content */}
        <div className="grid gap-8 mb-12 grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1fr_1fr]">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="space-y-3 max-w-prose">
              <img
                src={`${process.env.PUBLIC_URL || ''}/seleno/selenoreverse.png`}
                alt="Seleno Logo"
                className="h-12 sm:h-14 w-auto object-contain hover:drop-shadow-lg transition-all duration-300"
                loading="lazy"
              />
              <p className="text-sm sm:text-sm text-gray-300 leading-relaxed">
                {language === 'id'
                  ? companyInfo.description
                  : 'Seleno provides integrated IT solutions and advanced cybersecurity to build a resilient business ecosystem.'}
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-yellow-500 mb-6 flex items-center">
              <span className="w-1 h-1 bg-yellow-500 rounded-full mr-2"></span>
              {language === 'id' ? 'Navigasi' : 'Navigation'}
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <button type="button" onClick={() => scrollToSection('hero')} className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 flex items-center group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full mr-2 group-hover:bg-yellow-400 transition-colors"></span>
                  {t(language, 'header.home')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 flex items-center group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full mr-2 group-hover:bg-yellow-400 transition-colors"></span>
                  {t(language, 'header.about')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 flex items-center group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full mr-2 group-hover:bg-yellow-400 transition-colors"></span>
                  {t(language, 'header.services')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('partners')} className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 flex items-center group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full mr-2 group-hover:bg-yellow-400 transition-colors"></span>
                  {t(language, 'header.partners')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('clients')} className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 flex items-center group">
                  <span className="w-1.5 h-1.5 bg-gray-600 rounded-full mr-2 group-hover:bg-yellow-400 transition-colors"></span>
                  {t(language, 'header.clients')}
                </button>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-yellow-500 mb-6 flex items-center">
              <span className="w-1 h-1 bg-yellow-500 rounded-full mr-2"></span>
              {t(language, 'footer.products')}
            </h3>
            <ul className="space-y-3 text-sm">
              {products.map((product) => (
                <li key={product.id}>
                  <button
                    type="button"
                    onClick={() => goToProduct(product.slug)}
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 flex items-center group w-full text-left"
                  >
                    <span className="w-1.5 h-1.5 bg-gray-600 rounded-full mr-2 group-hover:bg-yellow-400 transition-colors flex-shrink-0"></span>
                    {product.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-yellow-500 mb-6 flex items-center">
              <span className="w-1 h-1 bg-yellow-500 rounded-full mr-2"></span>
              {language === 'id' ? 'Kontak' : 'Contact'}
            </h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start group">
                <Phone size={18} className="mr-3 mt-0.5 flex-shrink-0 text-yellow-500 group-hover:text-yellow-400 transition-colors" />
                <div className="text-gray-300">
                  {companyInfo.phone.map((phone, index) => (
                    <a
                      key={index}
                      href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
                      className="hover:text-yellow-400 transition-colors block"
                      aria-label={`Call ${phone}`}
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start group">
                <Mail size={18} className="mr-3 mt-0.5 flex-shrink-0 text-yellow-500 group-hover:text-yellow-400 transition-colors" />
                <div className="text-gray-300 text-xs">
                  {companyInfo.email.map((email, index) => (
                    <a
                      key={index}
                      href={`mailto:${email}`}
                      className="break-all hover:text-yellow-400 transition-colors block"
                      aria-label={`Email ${email}`}
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start group">
                <MapPin size={18} className="mr-3 mt-0.5 flex-shrink-0 text-yellow-500 group-hover:text-yellow-400 transition-colors" />
                <p className="text-gray-300 text-xs leading-relaxed">
                  {companyInfo.address}
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 my-10"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-xs text-center sm:text-left">
            <span className="font-semibold text-white">&copy; {new Date().getFullYear()} {companyInfo.fullName}</span> • {language === 'id' ? 'Semua hak dilindungi' : 'All rights reserved'}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200">
              {language === 'id' ? 'Kebijakan Privasi' : 'Privacy Policy'}
            </a>
            <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200">
              {language === 'id' ? 'Syarat Layanan' : 'Terms of Service'}
            </a>
            <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200">
              {language === 'id' ? 'Kebijakan Cookie' : 'Cookie Policy'}
            </a>

            {/* Language Toggle */}
            <div className="border-l border-gray-700 pl-4">
              <button
                type="button"
                onClick={toggleLanguage}
                className="px-3 py-1 bg-[#996522] bg-[linear-gradient(56deg,_rgba(153,101,34,1)_0%,_rgba(255,180,41,1)_100%)] text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300 text-xs uppercase tracking-wider"
                aria-label="Toggle language"
              >
                {language === 'id' ? 'ID' : 'EN'} | {language === 'id' ? 'EN' : 'ID'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

